import type { FC } from 'react';
import { Navigation } from './Navigation.tsx';
import { resolveImagePath } from '@/utils/imageMap.ts';
import headerData from '@/data/header.json';

export const Header: FC = () => {
  return (
    <header className="bg-white relative">
      {/* Top bar with contact info and social links */}
      <div className="bg-gray-800 text-white text-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-2">
            {/* Contact info */}
            <div className="flex items-center space-x-6">
              <a 
                href={headerData.contact.addressLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-yellow-400 hover:text-yellow-300 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                {headerData.contact.address}
              </a>
              <a 
                href={`mailto:${headerData.contact.email}`}
                className="flex items-center text-yellow-400 hover:text-yellow-300 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
                {headerData.contact.email}
              </a>
            </div>
            
            {/* Social links */}
            <div className="flex items-center space-x-4">
              {headerData.socialLinks.map((social) => (
                <a
                  key={social.platform}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-yellow-400 transition-colors"
                  aria-label={social.label}
                >
                  {social.platform === 'facebook' && (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  )}
                  {social.platform === 'yelp' && (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12.27 4.287a.96.96 0 0 0-.486.047l-.235.092c-1.894.736-3.52 1.828-4.723 3.226a.96.96 0 0 0-.201.513v.011c-.005.054-.005.11 0 .165.005.055.014.108.026.16.025.103.067.201.126.287.097.141.237.246.402.302l1.677.571c.25.085.523.067.759-.05a.96.96 0 0 0 .459-.55l.001-.003c.011-.04.018-.081.021-.123a.96.96 0 0 0-.016-.361l-.004-.02a.96.96 0 0 0-.124-.299l-.005-.008a.96.96 0 0 0-.253-.253l-.013-.008a.96.96 0 0 0-.369-.126h-.003a.96.96 0 0 0-.404.051l-.008.003a.96.96 0 0 0-.325.196c-.554-1.035-1.309-1.968-2.237-2.753l-.009-.008c.928-.785 1.683-1.718 2.237-2.753a.96.96 0 0 0 .325.196l.008.003a.96.96 0 0 0 .404.051h.003a.96.96 0 0 0 .369-.126l.013-.008a.96.96 0 0 0 .253-.253l.005-.008a.96.96 0 0 0 .124-.299l.004-.02a.96.96 0 0 0 .016-.361.96.96 0 0 0-.021-.123l-.001-.003a.96.96 0 0 0-.459-.55.96.96 0 0 0-.759-.05l-1.677.571a.96.96 0 0 0-.402.302.96.96 0 0 0-.126.287.96.96 0 0 0-.026.16.96.96 0 0 0 0 .165v.011a.96.96 0 0 0 .201.513c1.203 1.398 2.829 2.49 4.723 3.226l.235.092a.96.96 0 0 0 .486.047z"/>
                    </svg>
                  )}
                  {social.platform === 'instagram' && (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  )}
                </a>
              ))}
              
              {/* Google Reviews Badge */}
              <div className="flex items-center bg-white rounded px-2 py-1">
                <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span className="ml-1 text-black text-xs font-medium">Google</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <div className="flex-shrink-0">
              <a href="/" className="flex items-center">
                <img
                  src={resolveImagePath(headerData.logo.src)}
                  alt={headerData.logo.alt}
                  className="h-16 w-auto"
                />
              </a>
            </div>

            {/* Navigation */}
            <div className="hidden lg:flex flex-1 justify-center">
              <Navigation />
            </div>

            {/* CTA Phone Button */}
            <div className="flex-shrink-0">
              <a
                href={headerData.cta.href}
                className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-3 px-6 rounded-lg transition-colors flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                </svg>
                {headerData.cta.text}
              </a>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};